import {
  ActionGroup,
  Alert,
  Button,
  FormAlert,
  Modal,
  ModalVariant,
  Title,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

import { InstrumentSelector } from '@app/components/program/instrument_selector'
import { useInstruments } from '@app/domain/instrument/instrument.hooks'
import type { IInstrument } from '@app/domain/instrument/instrument.interface'
import type {
  IProgramInteraction,
  ProgramInteractionTypeId,
} from '@app/domain/program/program.interface'

interface Props {
  isModalOpen: boolean
  interaction?: IProgramInteraction
  interactionType: ProgramInteractionTypeId
  addOrUpdateInteraction: (
    selectedInstruments: string[],
    interactionType: ProgramInteractionTypeId,
    interactionId?: string,
  ) => void
  onCancel: () => void
  title: string
}

export const ModifyInteractionModal = function ({
  interaction,
  isModalOpen,
  interactionType,
  onCancel,
  addOrUpdateInteraction,
  title,
}: Props) {
  const { instruments } = useInstruments()
  const [allInstruments, setAllInstruments] = useState<IInstrument[]>([])
  const [selectedInstruments, setSelectedInstruments] = useState<IInstrument[]>(
    [],
  )

  const handleSetSelectedInstruments = useCallback(
    (selectedInstruments: IInstrument[]) => {
      setSelectedInstruments(selectedInstruments)
    },
    [setSelectedInstruments],
  )

  const handleAddInitialInteraction = useCallback(() => {
    addOrUpdateInteraction(
      selectedInstruments.map((it) => it.id),
      interactionType,
      interaction?.id,
    )
  }, [
    addOrUpdateInteraction,
    interaction,
    interactionType,
    selectedInstruments,
  ])

  useEffect(() => {
    if (interaction) {
      setSelectedInstruments(interaction.instruments)
    } else {
      setSelectedInstruments([])
    }
  }, [interaction, isModalOpen])

  useEffect(() => {
    setAllInstruments(instruments)
  }, [instruments])

  return (
    <Modal
      variant={ModalVariant.large}
      title={title}
      isOpen={isModalOpen}
      onClose={onCancel}
    >
      {selectedInstruments.length === 0 ? (
        <FormAlert>
          <Alert
            variant="warning"
            title="No instruments have been selected!"
            aria-live="polite"
            isInline
          />
        </FormAlert>
      ) : null}
      <Title headingLevel="h3" size="lg">
        Instruments
      </Title>
      <InstrumentSelector
        allInstruments={allInstruments}
        selectedInstruments={selectedInstruments}
        onSelectedUpdated={handleSetSelectedInstruments}
      />
      <ActionGroup style={{ float: 'right', marginTop: 10 }}>
        <Button
          key="cancel"
          variant="secondary"
          style={{ marginRight: 5 }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          key="confirm"
          variant="primary"
          onClick={handleAddInitialInteraction}
          isDisabled={selectedInstruments?.length === 0}
        >
          Save
        </Button>
      </ActionGroup>
    </Modal>
  )
}
